<template>
  <v-dialog v-model="localVisible" max-width="1000px" persistent>
    <v-card v-if="handicap">
      <v-card-title class="primary white--text">
        <span>{{ handicap.user_display }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- HEADER CON HANDICAP ACTUAL -->
        <v-row class="mb-6">
          <v-col cols="12" sm="4">
            <div class="text-center">
              <div class="handicap-large">
                {{ handicap.official_handicap }}
              </div>
              <div class="grey--text">Handicap Oficial</div>
              <TrendIndicator :trend="trend" />
            </div>
          </v-col>
          <v-col cols="12" sm="8">
            <v-list dense>
              <v-list-item>
                <v-list-item-title>Rondas incluidas:</v-list-item-title>
                <v-list-item-subtitle>{{ handicap.rounds_count }} (últimas 8 de 20)</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Última actualización:</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(handicap.updated_at) }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Usuario ID:</v-list-item-title>
                <v-list-item-subtitle>{{ handicap.user }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- GRÁFICO DE EVOLUCIÓN -->
        <div v-if="userScores.length" class="mb-6">
          <h3 class="mb-4">Evolución del Handicap</h3>
          <HandicapChart :scores="userScores" />
        </div>

        <!-- COMPARATIVA CON CLUB -->
        <div v-if="comparison" class="mb-6">
          <HandicapComparison :comparison="comparison" />
        </div>

        <!-- TABS -->
        <v-tabs v-model="activeTab">
          <!-- TAB: Últimas Rondas -->
          <v-tab>
            <v-icon left>mdi-history</v-icon>
            Últimas Rondas
          </v-tab>

          <!-- TAB: Auditoría (solo admin) -->
          <v-tab v-if="isAdmin">
            <v-icon left>mdi-clipboard-list</v-icon>
            Auditoría
          </v-tab>

          <!-- Tab Content: Últimas Rondas -->
          <v-tab-item>
            <div class="pa-4">
              <div v-if="loadingScores" class="text-center py-8">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <div v-else-if="!userScores.length" class="text-center py-8 grey--text">
                Sin rondas registradas
              </div>
              <v-data-table
                v-else
                :headers="scoresHeaders"
                :items="userScores.slice(0, 10)"
                hide-default-footer
                class="elevation-0"
                :row-class="getRowClass"
              >
                <template v-slot:item.created_at="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>
                <template v-slot:item.total_score="{ item }">
                  <strong>{{ item.total_score }}</strong>
                </template>
                <template v-slot:item.differential="{ item }">
                  <span :class="getDifferentialClass(item.differential)">
                    {{ item.differential > 0 ? '+' : '' }}{{ item.differential }}
                  </span>
                </template>
                <template v-slot:item.isIncludedInHandicap="{ item }">
                  <div v-if="item.isIncludedInHandicap" class="included-badge">
                    <v-icon small color="success">mdi-check</v-icon>
                    <span class="ml-1">{{ item.handicapIndex }}º</span>
                  </div>
                  <div v-else class="excluded-badge">
                    <v-icon small color="grey">mdi-close</v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>

          <!-- Tab Content: Auditoría -->
          <v-tab-item v-if="isAdmin">
            <div class="pa-4">
              <div v-if="loadingAudit" class="text-center py-8">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <div v-else-if="!auditLogs.length" class="text-center py-8 grey--text">
                Sin cambios registrados
              </div>
              <v-data-table
                v-else
                :headers="auditHeaders"
                :items="auditLogs"
                hide-default-footer
                class="elevation-0"
              >
                <template v-slot:item.changed_at="{ item }">
                  {{ formatDate(item.changed_at) }}
                </template>
                <template v-slot:item.old_value="{ item }">
                  <span class="error--text">{{ item.old_value }}</span>
                </template>
                <template v-slot:item.new_value="{ item }">
                  <span class="success--text">{{ item.new_value }}</span>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>
        </v-tabs>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">
          Cerrar
        </v-btn>
        <v-btn color="primary" @click="refreshDetail">
          <v-icon left>mdi-refresh</v-icon>
          Refrescar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import HandicapChart from './HandicapChart.vue'
import TrendIndicator from './TrendIndicator.vue'
import HandicapComparison from './HandicapComparison.vue'

export default {
  name: 'HandicapDetailDialog',
  components: {
    HandicapChart,
    TrendIndicator,
    HandicapComparison
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    handicap: {
      type: Object,
      default: null
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 0,
      loadingScores: false,
      loadingAudit: false,
      scoresHeaders: [
        { text: 'Fecha', value: 'created_at' },
        { text: 'Score', value: 'total_score' },
        { text: 'Diferencial', value: 'differential' },
        { text: '¿Incluida?', value: 'isIncludedInHandicap' }
      ],
      auditHeaders: [
        { text: 'Fecha', value: 'changed_at' },
        { text: 'Cambió por', value: 'changed_by_display' },
        { text: 'Valor Anterior', value: 'old_value' },
        { text: 'Valor Nuevo', value: 'new_value' }
      ]
    }
  },
  computed: {
    ...mapGetters('handicap', ['getUserScoresCount']),
    ...mapState('handicap', {
      selectedUserScores: 'selectedUserScores',
      auditLogs: 'auditLogs'
    }),
    localVisible: {
      get () {
        return this.visible
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    userScores () {
      if (!this.handicap) return []
      return this.selectedUserScores[this.handicap.user] || []
    },
    trend () {
      if (!this.handicap) return null
      return this.$store.getters['handicap/calculateTrend'](this.handicap.user)
    },
    comparison () {
      if (!this.handicap) return null
      return this.$store.getters['handicap/getPlayerComparison'](this.handicap.user)
    }
  },
  watch: {
    visible (newVal) {
      if (newVal && this.handicap) {
        this.loadDetail()
      }
    }
  },
  methods: {
    ...mapActions('handicap', ['fetchUserScores', 'fetchAuditLogs']),
    async loadDetail () {
      if (!this.handicap) return

      // Cargar scores
      this.loadingScores = true
      try {
        await this.fetchUserScores({
          userId: this.handicap.user,
          params: { limit: 20 }
        })
      } catch (error) {
        console.error('Error loading scores:', error)
      } finally {
        this.loadingScores = false
      }

      // Cargar auditoría (solo admin)
      if (this.isAdmin) {
        this.loadingAudit = true
        try {
          await this.fetchAuditLogs({
            golf_score__user_id: this.handicap.user,
            field_name: 'handicap'
          })
        } catch (error) {
          console.error('Error loading audit logs:', error)
        } finally {
          this.loadingAudit = false
        }
      }
    },
    async refreshDetail () {
      await this.loadDetail()
    },
    formatDate (date) {
      if (!date) return '--'
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getRowClass (item) {
      if (item.isIncludedInHandicap) {
        return 'row-included-in-handicap'
      }
      return ''
    },
    getDifferentialClass (differential) {
      if (differential < -1) return 'success--text' // Eagle
      if (differential === -1) return 'info--text' // Birdie
      if (differential === 0) return 'primary--text' // Par
      if (differential === 1) return 'warning--text' // Bogey
      return 'error--text' // Doble+
    }
  }
}
</script>

<style scoped lang="scss">
.handicap-large {
  font-size: 56px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 10px;
}

::v-deep .row-included-in-handicap {
  background-color: #e8f5e9 !important;

  td {
    font-weight: 500;
  }
}

.included-badge {
  display: flex;
  align-items: center;
  color: #2e7d32;
  font-weight: 600;
  font-size: 12px;
}

.excluded-badge {
  display: flex;
  align-items: center;
  color: #999;
}
</style>
